import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { env } from '@/env.mjs';
import { CLIENT_INSTANCE_HEADER, clientInstanceId } from '@/lib/client-instance-id';
import { LOCALE_OVERRIDE_HEADER, readLocaleOverride } from '@/lib/locale-override';
import { getLoginUrlFromClient } from '../utils/login-url';

const api = axios.create({
  baseURL: env.NEXT_PUBLIC_PUBLIC_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
    config.headers[CLIENT_INSTANCE_HEADER] = clientInstanceId;
  }

  const localeOverride = readLocaleOverride();
  if (localeOverride) {
    config.headers[LOCALE_OVERRIDE_HEADER] = localeOverride;
  }

  return config;
});

/**
 * Read-only API base class for handling GET and search operations.
 * This class provides common functionality for APIs that only need to read data.
 *
 * TGetAllResponse - The type of the response for the getAll and getAllPaginated methods.
 * TGetByIdResponse - The type of the response for the getById method.
 * TSearchBody - The type of the body for the search method. Defaults to object.
 * TSearchResponse - The type of the response for the search method. Defaults to { count: number; records: TGetAllResponse[] }.
 *
 * Example:
 * class PublicDocumentApi extends ReadOnlyApi<
 *   { id: number; title: string },
 *   { id: number; title: string; content: string },
 *   { query?: string; category?: string }
 * > {
 *   getPath() {
 *     return '/public/documents';
 *   }
 * }
 */
export class ReadOnlyApi<
  TGetAllResponse,
  TGetByIdResponse,
  TSearchBody = object,
  TSearchResponse = { count: number; records: TGetAllResponse[] },
> {
  get api() {
    return api;
  }

  getPath(): string {
    throw new Error('Not implemented');
  }

  // Generic request method to be used by children to extend the
  // class methods for specific use cases
  async request<T>(config: AxiosRequestConfig) {
    try {
      const res = await this.api.request<T>({
        ...config,
      });

      return res;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        window.location.href = getLoginUrlFromClient();
      } else if (err instanceof AxiosError && err.response?.data?.message) {
        // Server error we want to display to the user
        const error = new Error(err.response.data.message) as Error & { status: number };
        error.status = err.response.status;

        throw error;
      }

      throw err;
    }
  }

  async getAll(config?: AxiosRequestConfig) {
    const { data } = await this.request<TGetAllResponse[]>({
      method: 'GET',
      url: this.getPath(),
      ...config,
    });

    return data;
  }

  async getAllPaginated(
    config?: AxiosRequestConfig
  ): Promise<{ count: number; records: TGetAllResponse[] }> {
    const { data } = await this.request<{ count: number; records: TGetAllResponse[] }>({
      method: 'GET',
      url: this.getPath(),
      ...config,
    });

    return data;
  }

  async search(body: TSearchBody, config?: AxiosRequestConfig): Promise<TSearchResponse> {
    const { data } = await this.request<TSearchResponse>({
      method: 'POST',
      url: `${this.getPath()}/search`,
      data: body,
      ...config,
    });

    return data;
  }

  async getById(id: number, config?: AxiosRequestConfig) {
    const { data } = await this.request<TGetByIdResponse>({
      method: 'GET',
      url: `${this.getPath()}/${id}`,
      ...config,
    });

    return data;
  }
}

/**
 * CRUD API class that extends ReadOnlyApi to add write operations.
 * Inherits all read operations from ReadOnlyApi and adds create, update, delete, and submit methods.
 *
 * TGetAllResponse - The type of the response for the getAll method.
 * TGetByIdResponse - The type of the response for the getById method.
 * TCreateBody - The type of the body for the create method.
 * TUpdateBody - The type of the body for the update method.
 * TCreateResponse - The type of the response for the create method. Defaults to TGetByIdResponse.
 * TUpdateResponse - The type of the response for the update method. Defaults to TGetByIdResponse.
 * TSearchBody - The type of the body for the search method. Defaults to object.
 *
 * Example:
 * class DocumentTypeApi extends CrudApi<
 *   { id: number; name: string },
 *   { id: number; name: string, description: string },
 *   { name: string, description?: string },
 *   { name: string, description: string },
 *   { id: number; name: string, description: string, isNew: true },
 *   { id: number; name: string, description: string }
 * > {
 *   getPath() {
 *     return '/document-types';
 *   }
 * }
 */
export class CrudApi<
  TGetAllResponse,
  TGetByIdResponse,
  TCreateBody,
  TUpdateBody,
  TCreateResponse = TGetByIdResponse,
  TUpdateResponse = TGetByIdResponse,
  TSearchBody = object,
  TSearchResponse = { count: number; records: TGetAllResponse[] },
> extends ReadOnlyApi<TGetAllResponse, TGetByIdResponse, TSearchBody, TSearchResponse> {
  async create(body: TCreateBody, config?: AxiosRequestConfig) {
    const { data } = await this.request<TCreateResponse>({
      method: 'POST',
      url: this.getPath(),
      data: body,
      ...config,
    });

    return data;
  }

  async update(id: number, body: TUpdateBody, config?: AxiosRequestConfig) {
    const { data } = await this.request<TUpdateResponse>({
      method: 'PUT',
      url: `${this.getPath()}/${id}`,
      data: body,
      ...config,
    });

    return data;
  }

  async delete(id: number, config?: AxiosRequestConfig) {
    await this.request({
      method: 'DELETE',
      url: `${this.getPath()}/${id}`,
      ...config,
    });
  }
}
